import React from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  whitespace?: 'nowrap' | 'normal';
  children: React.ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-[#3b82f6] border-none shadow-lg shadow-[#3b82f6]/30 hover:shadow-xl hover:shadow-[#3b82f6]/40 hover:-translate-y-0.5 hover:bg-[#2563eb] text-white',
  secondary:
    'border border-white/10 bg-white/5 text-white hover:bg-white/10 hover:border-white/20',
  ghost: 'bg-transparent text-white/60 hover:text-white/90 hover:bg-white/5',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-xs',
  md: 'px-5 py-2.5 text-sm',
  lg: 'px-6 py-3 text-base',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  whitespace,
  disabled,
  children,
  ...props
}) => {
  const baseStyles =
    'font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed';
  const variantStyle = variantStyles[variant];
  const sizeStyle = sizeStyles[size];
  const hoverStyle =
    variant === 'secondary' ? 'hover:-translate-y-0.5' : '';
  const whitespaceStyle = whitespace === 'nowrap' ? 'whitespace-nowrap' : '';

  const classes = [
    baseStyles,
    variantStyle,
    sizeStyle,
    hoverStyle,
    whitespaceStyle,
  ].filter(Boolean).join(' ');

  return (
    <button
      className={classes}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

